import React, { useState, useEffect, useRef } from "react";
import BaseModal from "components/BaseModal";
import { useSession } from "hooks/useSession";
import { showMainToast } from "utils/showToast";

export default function PlanFormModal({ show, onClose, mode, userData, onSuccess }) {
  const [errors, setErrors]                   = useState({});
  const [editData, setEditData]               = useState(null);
  const [searchQuery, setSearchQuery]         = useState("");
  const [searchResults, setSearchResults]     = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDropdown, setShowDropdown]       = useState(false);
  const searchTimeout                         = useRef(null);
  const { userid } = useSession();

  const handleSearch = (query) => {
    setSearchQuery(query);
    setSelectedAccount(null);
    setShowDropdown(true);
    clearTimeout(searchTimeout.current);
    if (!query.trim()) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        const form = new FormData();
        form.append("action", "SEARCH_ACCOUNT");
        form.append("query", query);
        form.append("userid", userid);
        const res    = await fetch("https://api.mokshiq.in/plan_manager_action", {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          body: form,
        });
        const result = await res.json();
        setSearchResults(result.data || []);
      } catch (err) { console.error(err); }
    }, 300);
  };

  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    setSearchQuery(account.name);
    setShowDropdown(false);
    setSearchResults([]);
  };

  useEffect(() => {
    if (mode === "edit" && userData?.id) {
      const fetchById = async () => {
        try {
          const form = new FormData();
          form.append("action", "GETBYID");
          form.append("id", userData.id);
          form.append("userid", userid);
          const res    = await fetch("https://api.mokshiq.in/plan_manager_action", {
            method: "POST",
            headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
            body: form,
          });
          const result = await res.json();
          if (result.status) {
            setEditData(result.data);
            if (result.data.created_for_id) {
              setSelectedAccount({ id: result.data.created_for_id, name: result.data.created_for_name || "" });
              setSearchQuery(result.data.created_for_name || "");
            }
          }
        } catch (err) { console.error(err); }
      };
      fetchById();
    } else {
      setEditData(null);
      setSelectedAccount(null);
      setSearchQuery("");
    }
  }, [mode, userData]);

  const validateForm = () => {
    let newErrors = {};
    if (!document.getElementById("name")?.value.trim())      newErrors.name      = "Name is required.";
    if (!document.getElementById("plan_type")?.value.trim()) newErrors.plan_type = "Plan Type is required.";
    if (!selectedAccount)                                     newErrors.created_for = "Created For is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) { showMainToast("Please correct the errors before saving.", "danger"); return; }
    const formData = new FormData();
    formData.append("name",                     document.getElementById("name").value.trim());
    formData.append("plan_type",                document.getElementById("plan_type").value);
    formData.append("description",              document.getElementById("description").value.trim());
    formData.append("retailer_creation_charge", document.getElementById("retailer_creation_charge").value);
    formData.append("role_change_charge",       document.getElementById("role_change_charge").value);
    formData.append("is_active",                document.getElementById("is_active").value);
    formData.append("created_for_id",           selectedAccount?.id || "");
    formData.append("id",                       userData?.id || "");
    formData.append("action",                   "ADDUpdate");
    formData.append("userid",                   userid);
    try {
      const response = await fetch("https://api.mokshiq.in/plan_manager_action", {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        body: formData,
      });
      const result = await response.json();
      if (result.status) { showMainToast(result.data || "Success!", "success"); onClose(); if (onSuccess) onSuccess(); }
      else showMainToast(result.data || "Something went wrong!", "danger");
    } catch { showMainToast("Something went wrong!", "danger"); }
  };

  const isReady = mode === "add" || (mode === "edit" && editData !== null);

  return (
    <BaseModal
      show={show} onClose={onClose}
      title={mode === "edit" ? "Edit Plan" : "Add Plan"}
      footerButtons={[
        { label: "Cancel", className: "btn-secondary", dismiss: true },
        { label: mode === "edit" ? "Update" : "Save", className: "btn-primary", onClick: handleSave },
      ]}
    >
      <div className="card-body">
        {!isReady ? <div className="text-center py-4">Loading...</div> : (
          <form key={editData?.id || "new"} className="row g-3 app-form rounded-control">

            <div className="col-md-6">
              <label className="form-label">Plan Name</label>
              <input id="name" className={`form-control ${errors.name ? "is-invalid" : ""}`} defaultValue={editData?.name || ""} />
              {errors.name && <div className="text-danger">{errors.name}</div>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Plan Type</label>
              <select id="plan_type" className={`form-select ${errors.plan_type ? "is-invalid" : ""}`} defaultValue={editData?.plan_type || ""}>
                <option value="">-- Select Type --</option>
                <option value="admin">Admin</option>
                <option value="whitelabel">Whitelabel</option>
              </select>
              {errors.plan_type && <div className="text-danger">{errors.plan_type}</div>}
            </div>

            <div className="col-md-12">
              <label className="form-label">Description</label>
              <input id="description" className="form-control" defaultValue={editData?.description || ""} />
            </div>

            <div className="col-md-6">
              <label className="form-label">Retailer Creation Charge (₹)</label>
              <input id="retailer_creation_charge" type="number" className="form-control" defaultValue={editData?.retailer_creation_charge || "0"} />
            </div>

            <div className="col-md-6">
              <label className="form-label">Role Change Charge (₹)</label>
              <input id="role_change_charge" type="number" className="form-control" defaultValue={editData?.role_change_charge || "0"} />
            </div>

            {/* ✅ Searchable Created For */}
            <div className="col-md-6" style={{ position: "relative" }}>
              <label className="form-label">Created For</label>
              <input
                type="text"
                className={`form-control ${errors.created_for ? "is-invalid" : ""}`}
                placeholder="Search account by name..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchQuery && setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                autoComplete="off"
              />
              {errors.created_for && <div className="text-danger">{errors.created_for}</div>}
              {showDropdown && searchResults.length > 0 && (
                <ul style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999, background: "#fff", border: "1px solid #ddd", borderRadius: "4px", maxHeight: "200px", overflowY: "auto", listStyle: "none", margin: 0, padding: 0, boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }}>
                  {searchResults.map((account) => (
                    <li key={account.id} onMouseDown={() => handleSelectAccount(account)}
                      style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f0f0f0", fontSize: "14px" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                    >
                      {account.name} {account.phone ? `— ${account.phone}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">Status</label>
              <select id="is_active" className="form-select" defaultValue={editData?.is_active !== undefined ? String(editData.is_active) : "true"}>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

          </form>
        )}
      </div>
    </BaseModal>
  );
}